import * as React from "react";
import { Form, useFormState, useFormApi } from "informedv4";
import { TextBox } from "../../forms/informedv4/TextBoxV4";
import { useState } from "react";
import * as moment from "moment";
import { OverreadStatusIdEnum } from "../../misc/api/apiTypes";
import sampleSize from "lodash-es/sampleSize";
import { LocalQueryTable } from "../../misc/components/queryTable/LocalQueryTable";
import { QueryTableColumn } from "../../misc/components/queryTable/QueryTable";
import Modal, { ModalSize } from "../../misc/components/modal";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { LocalizedDateTime, DateTimeAccuracy } from "../../misc/components/LocalizedDateTime";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { CoughSession, AnalysisBatch } from "webportalclient";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { CoughAnalysisSetTypeEnum } from "../../misc/api/CoughAnalysisSetTypes";
import { IntegerValidator } from "../../forms/validation/NumericValidators";
import { Tooltip } from 'react-tooltip';

interface ProjectQAAnalysisSetTableProps {
    title: string;
    sessions: CoughSession[];
    onClose: () => void;
}

const ProjectQAAnalysisSetTable = ({ title, sessions, onClose }: ProjectQAAnalysisSetTableProps) => {

    const { fieldDisplayText } = useCurrentStudyConfigurationContext();

    return (
        <>
            <div className="modal-body">
                <div className="row">
                    <div className="col-md-12">
                        <h3 className="text-info">{title}</h3>
                    </div>
                    <div className="col-md-12">
                        <LocalQueryTable collection={sessions} defaultPageSize={10} rowUniqueIdentifier={row => row.id} query={{ Data: sessions }}>
                            <QueryTableColumn member="" header="Selected" map={row => row.sampled ? <i className="fas fa-check"></i> : null} sortable={false} filterable={false} />
                            <QueryTableColumn member="site" header="Site" map={row => row.visit.subject.site.number} sortable={false} filterable={false} />
                            <QueryTableColumn member="subject" header={fieldDisplayText.SubjectId} map={row => row.visit.subject.number} sortable={false} filterable={false} />
                            <QueryTableColumn member="visit" header="Visit" map={row => row.visit.name} sortable={false} filterable={false} />
                            <QueryTableColumn member="session" header="Session" map={row => row.name} sortable={false} filterable={false} />
                            <QueryTableColumn member="firstComplete" header="First Completed" map={row => <LocalizedDateTime time={row.analysisSets[0].firstComplete} accuracy={DateTimeAccuracy.Second} isTooltip={true} />} sortable={false} filterable={false} />
                        </LocalQueryTable>
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" onClick={onClose}>Back</button>
            </div>
        </>
    );
}

interface BlockSizeWarningProps {
    blockLength: number;
    enteredBlockSize: number
}

const BlockSizeWarning = ({ blockLength, enteredBlockSize }: BlockSizeWarningProps) => {
    if (blockLength < enteredBlockSize) {
    return (
        <>
            <i className="text-warning fas fa-exclamation-circle" aria-hidden="true" data-tooltip-id="blockSizeTooltip"></i>
            <Tooltip id="blockSizeTooltip" style={{ zIndex: 2500 }}>
                There are not enough sessions to fill the expected block size
            </Tooltip>
        </>);
    }

    return null;
}


export interface ProjectQABatchModalFormStructure {
    blockSize: number;
    sampleLength: number;
}


export interface CoughSessionSelected extends CoughSession {
    sampled: boolean;
}

export interface AnalysisBatchWithLastBlock extends AnalysisBatch {
    lastBlockDateTime: string;
}

export interface CreateProjectQABatchModalFormProps {
    previousBatch: AnalysisBatchWithLastBlock;
    onClose: () => void;
    onSave: (result: boolean) => void;
}

export const CreateProjectQABatchModalForm = ({ previousBatch, onClose, onSave }: CreateProjectQABatchModalFormProps) => {

    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();
    const [error, setError] = useState<string>();
    const [loadingState, setLoadingState] = useState<'loading' | 'saving' | 'preview' | 'initial'>('initial');
    const [currentBatchVisible, setCurrentBatchVisible] = useState(false);
    const [block, setBlock] = useState<CoughSessionSelected[]>([]);
    const { getFormState, validate } = useFormApi();
    const state = useFormState();

    const getAnalysisSetsByBatch = async (lastBatchDate: string, blockSize: number) => {
        const utcBatchDate = moment.parseZone(lastBatchDate).utc().toISOString();

        const result = await client.client.getCoughSessions(undefined,
            `analysissets/any(a: a/firstComplete gt ${utcBatchDate} and a/type eq '${CoughAnalysisSetTypeEnum.Analysis}' and (a/status eq '${OverreadStatusIdEnum.Accepted}' or a/status eq '${OverreadStatusIdEnum.Pending}')) and visit/subject/site/studyId eq ${study.id}`,
            undefined,
            `visit($expand=subject($select=number;$expand=site($select=number))),analysissets($filter=firstComplete gt ${utcBatchDate} and type eq '${CoughAnalysisSetTypeEnum.Analysis}' and (status eq '${OverreadStatusIdEnum.Accepted}' or status eq '${OverreadStatusIdEnum.Pending}');$orderby=firstComplete asc;$top=1;$select=id,firstComplete,type)`
        );
        return result.value.sort((a, b) => moment(a.analysisSets[0].firstComplete).diff(moment(b.analysisSets[0].firstComplete))).slice(0, blockSize);
    }

    const selectSets = async () => {

        const state = getFormState();
        const { blockSize, sampleLength } = state.values;

        if (!blockSize || !sampleLength) {
            setBlock([]);
            return;
        }

        setError(undefined);

        validate();
        const currentState = getFormState();

        if (currentState.invalid) {
            setError("Errors exist on the page");
            return;
        }

        if (blockSize < sampleLength) {
            setBlock([]);
            setError("Sample size should be less than Block size");
            return;
        }

        if (blockSize < 0 || sampleLength < 0) {
            setBlock([]);
            setError("Block size and Sample size should be greater than 0");
            return;
        }

        setLoadingState('loading');

        const result = await getAnalysisSetsByBatch(previousBatch?.lastBlockDateTime ?? "2000-01-01", blockSize as number);
        const sampledValues = sampleSize(result, sampleLength);
        setBlock(result.map(v => {
            return {
                ...v,
                sampled: sampledValues.map(x => x.id).includes(v.id) ? true : false,
            }
        }));
        setLoadingState('preview');
    }

    const createBatch = async () => {

        setLoadingState('saving');
        setError(undefined);
        
        const name = `PROJECT_QA_BATCH_${moment().format('YYYYMMDDHHmmss')}`;

        try {
            const result = await client.client.postAnalysisBatch({
                name: name,
                includedSessions: block.map(s => s.id),
                selectedSessions: block.filter(s => s.sampled).map(s => s.id),
                batchLastAnalysisSetId: block[block.length - 1].analysisSets[0].id,
            });
            result && result.id ? onSave(true) : onSave(false);
        }
        catch (ex) {
            setError(ex.Message);
            setLoadingState('preview');
            return;
        }

        onClose();
    }

    const handleBlockViewToggle = () => setCurrentBatchVisible(!currentBatchVisible);

    const contentVisible = currentBatchVisible
        ? 'none'
        : 'block';

    return (
        <> 
            <div style={{ display: contentVisible }} className="modal-body">
                <div>
                <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>{error}</Alert>
                    <div className="row">
                        <div className="form-group col-md-6">
                            <label htmlFor="blockSize">Block size</label>
                            <TextBox name="blockSize" id="blockSize" field="blockSize" type="number" min="0" onBlur={() => selectSets()} validate={(v) => IntegerValidator(v)} />
                        </div>

                        <div className="form-group col-md-6">
                            <label htmlFor="sampleLength">Sample size</label>
                            <TextBox name="sampleLength" id="sampleLength" field="sampleLength" type="number" min="0" onBlur={() => selectSets()} validate={(v) => IntegerValidator(v)} />
                        </div>

                        {previousBatch?.lastBlockDateTime !== undefined && previousBatch?.lastBlockDateTime !== null ?
                            <>
                                <div className="form-group col-md-12">
                                    <h3 className="text-primary">Previous Batch</h3>
                                    <label htmlFor="previousBatch">Generated:</label> <LocalizedDateTime time={previousBatch?.createdDateTime} accuracy={DateTimeAccuracy.Minute} isTooltip={true} />                                
                                </div>

                                <div className="form-group col-md-12">
                                    <label htmlFor="previousBatch">Last Session Date:</label> <LocalizedDateTime time={previousBatch?.lastBlockDateTime} accuracy={DateTimeAccuracy.Minute} isTooltip={true} />
                                </div>
                            </> : null}

                        <div className="form-group col-md-12" style={{ paddingTop: "40px" }}>
                            {loadingState === 'loading' ? <LoadingWithMessage message="Preparing analysis batch" /> : null}
                            {loadingState === 'saving' ? <LoadingWithMessage message="Creating analysis batch" /> : null}
                            {loadingState === 'preview' ? <table className="table">
                                <tbody>
                                    <tr>
                                        <th>Selection Block</th>
                                        <td data-automationid="currentBlockSize">{block.filter(x => x.sampled).length} / {block.length} <BlockSizeWarning blockLength={block.length} enteredBlockSize={state.values.blockSize as number} /></td>
                                        <td className="text-right"><button className="btn btn-link" data-automationid="viewCurrentBlock" onClick={handleBlockViewToggle}>View sessions</button></td>
                                    </tr>
                                </tbody>
                            </table> : null}
                        </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-primary" disabled={loadingState !== 'preview' || block.length === 0} onClick={createBatch} data-automationid="QCBatchCreateButton">Create</button>
                    <button className="btn btn-primary btn-outline" onClick={onClose} data-automationid="QCBatchCancelButton">Cancel</button>
                </div>
            </div>
            {currentBatchVisible ? <ProjectQAAnalysisSetTable sessions={block} title="Current Sessions" onClose={handleBlockViewToggle} /> : null}
        </>
    );
}

interface CreateProjectQABatchModalProps {
    onSave: (result: boolean) => void;
    onClose: () => void;
}

export const CreateProjectQABatchModal = ({ onClose, onSave }: CreateProjectQABatchModalProps) => {
    const [previousBatch, setPreviousBatch] = React.useState<AnalysisBatchWithLastBlock | undefined>();
    const [analysisBatches, setAnalysisBatches] = useState<AnalysisBatch[]>();
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();

    React.useEffect(() => {
        const getInit = async () => {
            const existingAnalysisBatches = await client.client.getAnalysisBatches(undefined, `studyId eq ${study.id} and sessionTypeId eq '${SessionTypes.Cough}'`, `createdDateTime desc`);
            
            const previousBatchId = existingAnalysisBatches.value.length > 0
                ? existingAnalysisBatches.value[0]
                : null;

            let lastBlockDateTime = null;
            if (previousBatchId?.batchLastAnalysisSetId !== null && previousBatchId?.batchLastAnalysisSetId !== undefined)
                lastBlockDateTime = (await client.client.getCoughAnalysisSet(previousBatchId.batchLastAnalysisSetId, "firstComplete")).firstComplete;

            setPreviousBatch({
                ...previousBatchId,
                lastBlockDateTime: lastBlockDateTime,
            });
            setAnalysisBatches(existingAnalysisBatches.value);
        }

        getInit();
    }, []);

    if (analysisBatches === undefined)
        return (
            <Modal header="Create Project QA Batch" show={true} size={ModalSize.Normal}>
                <div className="modal-body">
                    <LoadingWithMessage message="Loading Project QA Selection..." />
                </div>
            </Modal>
        );

    return (
        <Modal header="Create Project QA Batch" show={true} size={ModalSize.Normal}>
            <Form validateOn="submit">
                <CreateProjectQABatchModalForm previousBatch={previousBatch} onClose={onClose} onSave={onSave} />
            </Form>
        </Modal>
    );
};
